import { useQuery } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import { Link } from 'react-router';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { type Column, DataTable } from '../../components/ui/DataTable.js';
import { ListSearch } from '../../components/ui/ListSearch.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { RowActions } from '../../components/ui/RowActions.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useConfirmedMutation } from '../../hooks/useConfirmedMutation.js';
import { useListControls } from '../../hooks/useListControls.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';

/** One screenful. Matches the other paginated admin lists rather than inventing a third size. */
const PAGE_SIZE = 20;

interface Location {
  _id: string;
  name: string;
  city_name?: string;
  country_name?: string;
  is_active: boolean;
  /** 416 (#1431): the drawn geo-fence, when there is one. */
  zone?: { polygon?: unknown[] } | null;
}

interface LocationsResponse {
  items: Location[];
}

export function LocationListPage() {
  // 063 (FR-013): locations are platform-wide; only an administrator may add one.
  const ability = useAbility();
  const canCreate = ability.can('create', 'Location');
  // 159 (#14): its own ability, as `create` is separate from `update` elsewhere in the panel.
  const canDelete = ability.can('delete', 'Location');

  /**
   * 159 (#14): remove a pickup location.
   *
   * A SOFT delete — the platform stamps `deleted_at` and keeps the record, so bookings that
   * referenced the location still resolve. It also REFUSES with 409 when the location has future
   * active bookings, and that refusal is deliberately not restated here: `RowActions` keeps its
   * dialog open on a rejected action and shows the platform's own sentence. The panel does not
   * know which locations are booked and must not pretend to.
   */
  const remove = useConfirmedMutation({
    mutationFn: (id: string) => api.delete(`/locations/${id}`),
    invalidateKey: RESOURCE_KEYS.location.list,
  });

  // 159: built inside the component now — the previous module-level array could reach neither the
  // mutation nor the ability. Every column below is unchanged.
  const columns: Column<Location>[] = [
    { key: 'name', header: 'Name' },
    { key: 'city_name', header: 'City' },
    { key: 'country_name', header: 'Country' },
    {
      key: 'is_active',
      header: 'Status',
      render: (row) => (
        <Badge variant={row.is_active ? 'success' : 'default'}>
          {row.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    // 416 (#1431, mock-up screen 7): which campuses are measured. "Not set" means rentals picked up there are not.
    {
      key: 'zone',
      header: 'Zone',
      render: (row) => {
        const corners = row.zone?.polygon?.length ?? 0;
        return corners >= 3 ? (
          <Badge variant="success">Set · {corners} corners</Badge>
        ) : (
          <Badge variant={row.is_active ? 'warning' : 'default'}>Not set</Badge>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <RowActions
          actions={[
            {
              key: 'edit',
              label: 'Edit',
              href: `/locations/${row._id}/edit`,
              icon: <Pencil className="h-4 w-4" />,
            },
            canDelete && {
              key: 'delete',
              label: 'Delete',
              variant: 'danger' as const,
              confirmMessage: `Remove ${row.name} from the pickup locations? It stops being offered for new bookings. A location with future active bookings cannot be removed.`,
              onClick: () => remove.run(row._id),
              icon: <Trash2 className="h-4 w-4" />,
            },
          ]}
        />
      ),
    },
  ];

  // 162 (#21): a pager and a search box on a list that had neither.
  const { page, setPage, searchInput, setSearchInput, search } = useListControls();

  const listPath = (() => {
    // 335 (FR-002): inactive locations too, so a switched-off one can be found and switched back on. The platform
    // honours this only for staff who may edit locations; the customer app never sends it.
    const params = new URLSearchParams({
      page: String(page),
      limit: String(PAGE_SIZE),
      include_inactive: 'true',
    });
    // Only when there is something to filter by: a cleared box must show the whole list, not an
    // empty one (FR-004).
    if (search) params.set('q', search);
    return `/locations?${params.toString()}`;
  })();

  const { data, isLoading, error } = useQuery({
    queryKey: [...RESOURCE_KEYS.location.list, page, search],
    queryFn: () => api.get<LocationsResponse & { pages: number }>(listPath),
    // 063 (FR-010): a refusal is not a transient fault. Without this the caller
    // reads the empty state through three retries before the truth arrives.
    retry: false,
  });

  return (
    <div>
      <PageHeader
        title="Locations"
        description="Manage pickup and dropoff points"
        actions={
          canCreate ? (
            <Link to="/locations/new">
              <Button>Add Location</Button>
            </Link>
          ) : undefined
        }
      />
      <ListSearch
        value={searchInput}
        onChange={setSearchInput}
        label="Search locations by name or city"
        testId="location-search"
      />
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        loading={isLoading}
        keyExtractor={(row) => row._id}
        emptyMessage="No locations found"
        error={error as { status?: number } | null}
        pagination={{
          page: page,
          totalPages: data?.pages ?? 1,
          onPageChange: setPage,
        }}
      />
    </div>
  );
}
